import React, { useState, useEffect } from 'react'
import ChatResponse from '../../ChatResponse'
import UserActionButton from '../UserActionButton'
import DLoaderWriting from '@/components/Global/DLoaderWriting'
import UploadFile from '../UploadFile';
import { DateTime } from 'luxon';
import SoundResponseIcon from '../Icons/SoundResponseIcon';
import clsx from 'clsx';
import DButtonIcon from '@/components/Global/DButtonIcon';
import FileIcon from '@/components/Global/Icons/FileIcon';
import DSpinner from '@/components/Global/DSpinner';

const ChatMessage = ({
  role = 'assistant', // 'assistant', 'user'
  message = '',
  className = '',
  isFirstMessage = false,
  isLastMessage = false,
  timestamp = null,
  leadsGenMode = false,
  status = 'complete', // 'loading', 'typing', 'complete'
  type = '', // 'welcome_message', 'info', 'normal', etc.
  agent = {},
  images = [], // Array of image URLs or file objects
  files = [], // Array of file objects with name and url
  onFileClick = null, // Handler for file clicks
  isFadingOut = false, // Flag for fade out animation
  isFadingIn = false, // Flag for fade in animation
  responseBackground = '',
  handleFooterButton = null, // Handler for footer button actions
  messageId = null, // Message ID for footer actions
  reaction = '', // Current reaction state
  readonly = false, // Whether the message is readonly
  messageIndex = 0, // Index of the message for animations
  hideFooter = false, // Whether to hide footer actions
  isPreviewMode = false, // Whether in preview mode
  isInHumanHandoverApp = false, // Whether in human handover app
  interactingWithLiveAgent = false, // Whether currently interacting with live agent
  pollResponse = false, // Whether polling for live agent responses
  isLastAssistantMessage = false, // Whether this is the last assistant message in the conversation
  isLastWelcomeMessage = false, // Whether this is the last welcome_message in a sequence
  config = {}, // Configuration object containing show_play_button setting
  place = '', // Place of the message
  showSources = [], // Array of source IDs
  sourcesLoading = [], // Array of source loading states
  isInApp = false, // Whether in app context
  ...props
}) => {

  const isLeftSide = (role === 'assistant' && type !== 'info') || role === 'agent';
  const isUser = role === 'user';
  const isInfoType = type === 'info';

  // State to trigger re-renders for timestamp updates
  const [currentTime, setCurrentTime] = useState(DateTime.local());

  // Auto-update timestamp every minute
  useEffect(() => {
    if (!timestamp) return;

    const interval = setInterval(() => {
      setCurrentTime(DateTime.local());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [timestamp]);

  // Check if this is a human handover related message
  const isHumanHandoverMessage = type === 'human_handover_message' ||
    (type === 'message' && (interactingWithLiveAgent || pollResponse));


  // Format timestamp to relative time (e.g., "1min ago", "2h ago")
  // Use luxon to convert from UTC to local and display relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';

    let dt;
    if (typeof timestamp === 'number') {
      // Assume ms since epoch UTC
      dt = DateTime.fromMillis(timestamp, { zone: 'utc' });
    } else if (typeof timestamp === 'string') {
      // Try ISO or ms string
      const asNumber = Number(timestamp);
      if (!isNaN(asNumber)) {
        dt = DateTime.fromMillis(asNumber, { zone: 'utc' });
      } else {
        dt = DateTime.fromISO(timestamp, { zone: 'utc' });
      }
    } else if (timestamp instanceof Date) {
      dt = DateTime.fromJSDate(timestamp, { zone: 'utc' });
    } else {
      return '';
    }

    // Convert to local time zone
    const localDt = dt.setZone(currentTime.zoneName);

    // Calculate the difference in seconds
    const diffInSeconds = currentTime.diff(localDt, 'seconds').seconds;

    if (diffInSeconds < 60) {
      return 'less than 1 min';
    }

    return localDt.toRelative({ base: currentTime }) || '';
  };

  // Special rendering for info type messages
  if (isInfoType) {
    // Determine animation class based on fade flags
    let animationClass = 'animate-fadeIn';
    if (isFadingOut) {
      animationClass = 'animate-fadeOut';
    } else if (isFadingIn) {
      animationClass = 'animate-fadeIn';
    }

    return (
      <div className={`flex flex-col w-full ${animationClass}`}>
        <div className="flex py-v3-sm px-0 pb-v3-sm justify-center items-center gap-v3-sm self-stretch">
          <div className="h-px bg-v3-purple-primary flex-1"></div>
          <span className="text-v3-purple-primary font-inter-variable text-xs font-medium leading-16">
            {message}
          </span>
          <div className="h-px bg-v3-purple-primary flex-1"></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`
        bubble-chat-message
        flex flex-col
        ${role === 'assistant' && type === 'welcome_message' && !isLastWelcomeMessage ? 'mb-[2px]' : 'mb-v3-sm'}
        animate-fadeIn
        ${isLeftSide ? 'items-start pr-v3-lg' : 'items-end'}
        ${className}
      `}
      {...props}
    >
      {isLeftSide ? (
        <>
          {/* Wrapper for agent profile pic and message content */}
          <div className="flex items-start gap-v3-xs self-stretch">
            
            {/* Message content container */}
            <div className="bubble-message-content flex flex-col">
              {/* Show message content only when not loading */}
              {status !== 'loading' && message !== '' && (
                <div className="animate-fadeIn">
                  <ChatResponse message={message}  responseBackground={responseBackground} />
                </div>
              )}

              {/* Show files/images when present and not loading */}
              {status !== 'loading' && (images.length > 0 || files.length > 0) && (
                <div className="animate-fadeIn mt-v3-xs">
                  <div className="flex flex-wrap gap-v3-xs">
                    {/* Separate images from files based on file extension */}
                    {(() => {
                      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
                      const actualImages = images.filter(url => {
                        const extension = url.split('.').pop()?.toLowerCase();
                        return imageExtensions.includes(extension);
                      });
                      const documentFiles = images.filter(url => {
                        const extension = url.split('.').pop()?.toLowerCase();
                        return !imageExtensions.includes(extension);
                      });

                      return (
                        <>
                          {/* Display actual images */}
                          {actualImages.map((image, index) => {
                      // Extract filename from URL or create a default name
                      let filename;
                      try {
                        const urlParts = image.split('/');
                        const lastPart = urlParts[urlParts.length - 1];
                        // Check if the last part has a file extension
                        if (lastPart && lastPart.includes('.')) {
                          filename = lastPart;
                        } else {
                          // If no extension, try to get a meaningful name from URL
                          const meaningfulPart = urlParts.find(part =>
                            part.includes('.') && (part.includes('jpg') || part.includes('png') || part.includes('jpeg') || part.includes('gif'))
                          );
                          filename = meaningfulPart || `image-${index + 1}.jpg`;
                        }
                      } catch (error) {
                        filename = `image-${index + 1}.jpg`;
                      }

                      return (
                        <UploadFile
                          key={`image-${index}`}
                          filename={filename}
                          variant="download"
                          className="bg-v3-black-card"
                          onClick={() => onFileClick && onFileClick(image, 'image')}
                        />
                      );
                    })}

                          {/* Display document files */}
                          {documentFiles.map((fileUrl, index) => {
                            // Extract filename from URL or create a default name
                            let filename;
                            try {
                              const urlParts = fileUrl.split('/');
                              const lastPart = urlParts[urlParts.length - 1];
                              // Decode URL-encoded characters
                              filename = decodeURIComponent(lastPart);
                            } catch (error) {
                              filename = `file-${index + 1}.txt`;
                            }

                            return (
                              <UploadFile
                                key={`file-${index}`}
                                filename={filename}
                                variant="download"
                                className="bg-v3-black-card"
                                onClick={() => onFileClick && onFileClick(fileUrl, 'file')}
                              />
                            );
                          })}
                        </>
                      );
                    })()}

                    {/* Display files using UploadFile component (for files prop) */}
                    {files.map((file, index) => (
                      <UploadFile
                        key={`file-prop-${index}`}
                        filename={file.name || `file-${index + 1}`}
                        variant="download"
                        className="bg-v3-black-card"
                        onClick={() => onFileClick && onFileClick(file.url || file, 'file')}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Show loading state when status is loading */}
              {status === 'loading' && (
                <div
                  className="animate-fadeIn"
                  style={{
                    color: 'var(--v3-purple-primary)',
                    backgroundColor: 'transparent',
                  }}
                >
                  <div className="flex items-center justify-start gap-v3-xs h-6">
                    <DLoaderWriting />
                  </div>
                </div>
              )}

              {/* Show metadata div for the last assistant message (or all messages in chat_record) */}
              {status !== 'loading' && timestamp && type !== 'welcome_message' && role === 'assistant' && (isLastAssistantMessage || place === 'chat_record' || isInApp)  && (
                <div
                  className="flex px-v3-sm items-center justify-between gap-v3-xs mt-v3-xs text-v3-black-placeholder font-inter-variable font-medium text-xs leading-16 animate-fadeInUp max-w-full"
                >
                 {(isLastAssistantMessage || place === 'chat_record') && <div className='flex gap-v3-xs items-center whitespace-nowrap'>
                  {config?.message_disclaimer_enabled && <span className='truncate max-w-[150px] '>{agent?.agent_name || config?.message_disclaimer_text || "Dante AI"}</span>}
                  {config?.message_disclaimer_enabled && <div className='w-1 h-1 bg-v3-black-placeholder rounded-full flex-shrink-0'></div>}
                  {config?.message_disclaimer_enabled && <span className='truncate max-w-[150px]'>{agent?.agent_title || "AI Agent"}</span>}
                  {config?.message_disclaimer_enabled && <div className='w-1 h-1 bg-v3-black-placeholder rounded-full flex-shrink-0'></div>}
                  {formatRelativeTime(timestamp) && (
                        <span className='flex-shrink-0'>{formatRelativeTime(timestamp)}</span>
                    )}
                 </div>}

                  {/* Sound icon for playing message - only for assistant messages that are complete and not special message types */}
                  <div className="flex items-center justify-end w-full">
                    {handleFooterButton &&
                   messageId &&
                   role === 'assistant' &&
                   status === 'complete' &&
                   type !== 'welcome_message' &&
                   type !== 'agent_connecting' &&
                   type !== 'info' &&
                   !isHumanHandoverMessage &&
                   !hideFooter &&
                   !isPreviewMode &&
                   config?.show_play_button !== false && (
                    <button
                      disabled={readonly}
                      onClick={() => handleFooterButton({ message_id: messageId, action: 'play_message' })}
                      className={clsx(
                        'ml-v3-xs p-1 rounded-v3-xs transition-colors duration-200',
                        'hover:bg-v3-black-card',
                        '!size-5',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        {
                          'text-v3-purple-primary': reaction === 'play_message',
                          'text-v3-black-placeholder': reaction !== 'play_message',
                        },
                        messageIndex === 0 ? 'animate-fadeIn' : 'animate-fadeIn'
                      )}
                      title="Play message"
                    >
                      <SoundResponseIcon className='w-full h-full' />
                    </button>
                  )}
                    {(() => {
                      const shouldShowSources = (place === 'chat_record' || isInApp) && handleFooterButton && messageId && role === 'assistant' && status === 'complete' && type !== 'welcome_message' && type !== 'agent_connecting' && type !== 'info' && !isHumanHandoverMessage && !hideFooter && !isPreviewMode;
                      return shouldShowSources ? (
                        <DButtonIcon
                          disabled={readonly}
                          className={clsx({
                            'sources-icon': true,
                            'text-[var(--dt-color-element-20)]': true,
                            '!size-5': true,
                            'hover:bg-[var(--dt-color-element-2)]': true,
                            'disabled:opacity-50 disabled:cursor-not-allowed': true,
                            'self-end': true,
                          }, messageIndex === 0 ? 'animate-first-message-icons-appear' : 'animate-source-icon-appear')}
                          onClick={() => handleFooterButton({ message_id: messageId, action: 'sources' })}
                        >
                          {sourcesLoading?.includes(messageId) ? (
                            <DSpinner />
                          ) : (
                            <FileIcon className='size-3'/>
                          )}
                        </DButtonIcon>
                      ) : null;
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : isUser ? (
        <div className="flex flex-col items-end gap-v3-xs pl-v3-lg">
          {/* User message content */}

          {message !== '' && (
            <UserActionButton
              variant="filled"
              label={message}
              showIcon={false}
              type="sent"
              className='!py-[12px]'
            />
          )}

          {/* User files/images */}
          {(images.length > 0 || files.length > 0) && (
            <div className="flex flex-wrap gap-v3-xs justify-end">
              {/* Display images using UploadFile component */}
              {images.map((image, index) => {
                // Extract filename from URL or create a default name
                let filename;
                try {
                  const urlParts = image.split('/');
                  const lastPart = urlParts[urlParts.length - 1];
                  // Check if the last part has a file extension
                  if (lastPart && lastPart.includes('.')) {
                    filename = lastPart;
                  } else {
                    // If no extension, try to get a meaningful name from URL
                    const meaningfulPart = urlParts.find(part =>
                      part.includes('.') && (part.includes('jpg') || part.includes('png') || part.includes('jpeg') || part.includes('gif'))
                    );
                    filename = meaningfulPart || `image-${index + 1}.jpg`;
                  }
                } catch (error) {
                  filename = `image-${index + 1}.jpg`;
                }

                return (
                  <UploadFile
                    key={`user-image-${index}`}
                    filename={filename}
                    variant="download"
                    onClick={() => onFileClick && onFileClick(image, 'image')}
                  />
                );
              })}

              {/* Display files using UploadFile component */}
              {files.map((file, index) => (
                <UploadFile
                  key={`user-file-${index}`}
                  filename={file.name || `file-${index + 1}`}
                  variant="download"
                  onClick={() => onFileClick && onFileClick(file.url || file, 'file')}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default ChatMessage